import type { ReelSymbolId } from './types';

/**
 * Exact circular strips from the active Legacy runtime. Their order is mathematics,
 * not presentation data. Every strip has 58 stops.
 */
export const REEL_STRIPS = [
  [
    'JACK', 'QUEEN', 'KING', 'JACK', 'QUEEN', 'ACE', 'JACK', 'SCATTER', 'QUEEN', 'JACK',
    'KING', 'ACE', 'JACK', 'QUEEN', 'KING', 'ACE', 'JACK', 'QUEEN', 'KING', 'ACE',
    'JACK', 'QUEEN', 'WILD', 'KING', 'ACE', 'TW', 'JINZO', 'JACK', 'QUEEN', 'KING',
    'ACE', 'TW', 'JINZO', 'DM', 'JACK', 'QUEEN', 'SCATTER', 'KING', 'ACE', 'TW',
    'JINZO', 'DM', 'JACK', 'QUEEN', 'KING', 'ACE', 'TW', 'JINZO', 'DM', 'BEWD',
    'JACK', 'QUEEN', 'KING', 'ACE', 'TW', 'JINZO', 'DM', 'BEWD',
  ],
  [
    'JACK', 'QUEEN', 'KING', 'JACK', 'QUEEN', 'ACE', 'JACK', 'QUEEN', 'KING', 'SCATTER',
    'ACE', 'JACK', 'QUEEN', 'KING', 'ACE', 'JACK', 'QUEEN', 'KING', 'ACE', 'JACK',
    'QUEEN', 'KING', 'ACE', 'TW', 'JINZO', 'WILD', 'JACK', 'QUEEN', 'KING', 'ACE',
    'TW', 'JINZO', 'DM', 'JACK', 'QUEEN', 'KING', 'ACE', 'TW', 'SCATTER', 'JINZO',
    'DM', 'JACK', 'QUEEN', 'KING', 'ACE', 'TW', 'JINZO', 'DM', 'BEWD', 'JACK',
    'QUEEN', 'KING', 'ACE', 'TW', 'JINZO', 'DM', 'BEWD', 'JACK',
  ],
  [
    'JACK', 'QUEEN', 'KING', 'JACK', 'ACE', 'QUEEN', 'JACK', 'KING', 'ACE', 'JACK',
    'QUEEN', 'SCATTER', 'KING', 'ACE', 'JACK', 'QUEEN', 'KING', 'ACE', 'JACK', 'QUEEN',
    'KING', 'ACE', 'TW', 'JINZO', 'JACK', 'QUEEN', 'KING', 'WILD', 'ACE', 'TW',
    'JINZO', 'DM', 'JACK', 'QUEEN', 'KING', 'ACE', 'TW', 'JINZO', 'DM', 'JACK',
    'SCATTER', 'QUEEN', 'KING', 'ACE', 'TW', 'JINZO', 'DM', 'BEWD', 'JACK', 'QUEEN',
    'KING', 'ACE', 'TW', 'JINZO', 'DM', 'BEWD', 'JACK', 'QUEEN',
  ],
  [
    'JACK', 'QUEEN', 'ACE', 'JACK', 'QUEEN', 'KING', 'JACK', 'ACE', 'SCATTER', 'JACK',
    'QUEEN', 'KING', 'ACE', 'JACK', 'QUEEN', 'KING', 'ACE', 'JACK', 'QUEEN', 'KING',
    'ACE', 'TW', 'JINZO', 'JACK', 'WILD', 'QUEEN', 'KING', 'ACE', 'TW', 'JINZO',
    'DM', 'DM', 'QUEEN', 'KING', 'ACE', 'TW', 'JINZO', 'SCATTER', 'JACK', 'JACK',
    'QUEEN', 'KING', 'ACE', 'TW', 'JINZO', 'DM', 'BEWD', 'JACK', 'QUEEN', 'KING',
    'ACE', 'TW', 'JINZO', 'DM', 'BEWD', 'JACK', 'QUEEN', 'KING',
  ],
  [
    'JACK', 'QUEEN', 'KING', 'JACK', 'QUEEN', 'ACE', 'JACK', 'QUEEN', 'KING', 'JACK',
    'SCATTER', 'ACE', 'JACK', 'QUEEN', 'KING', 'ACE', 'JACK', 'QUEEN', 'KING', 'ACE',
    'TW', 'JINZO', 'JINZO', 'QUEEN', 'KING', 'ACE', 'WILD', 'TW', 'DM', 'DM',
    'JACK', 'QUEEN', 'KING', 'ACE', 'TW', 'JINZO', 'DM', 'JACK', 'QUEEN', 'SCATTER',
    'KING', 'ACE', 'TW', 'JINZO', 'DM', 'BEWD', 'JACK', 'QUEEN', 'KING', 'ACE',
    'TW', 'JINZO', 'DM', 'BEWD', 'JACK', 'QUEEN', 'KING', 'ACE',
  ],
] as const satisfies readonly (readonly ReelSymbolId[])[];

/**
 * Extreme keeps reels 1-4 on the proven 58-stop layout. Reels 1 and 3 replace
 * one low symbol with a third safely spaced Scatter. Reel 5 does the same and
 * adds a compact four-stop tail (JACK, QUEEN, SCATTER, KING). This raises the
 * exact trigger probability to 2.2167297106% (about 1 in 45.11 base spins)
 * while reinforcing common paying symbols instead of weakening the base hit
 * rate.
 *
 * WILD and premium inventories remain unchanged. The separate Extreme
 * free-spin weighting is defined below. Scatter distances are >= 3 circular
 * stops, so a reel can still never show more than one Scatter in its 3-row
 * window.
 */
export const EXTREME_REEL_STRIPS = [
  REEL_STRIPS[0].map((symbol, index) => index === 20 ? 'SCATTER' : symbol),
  [...REEL_STRIPS[1]],
  REEL_STRIPS[2].map((symbol, index) => index === 25 ? 'SCATTER' : symbol),
  [...REEL_STRIPS[3]],
  [
    ...REEL_STRIPS[4].map((symbol, index) => index === 30 ? 'SCATTER' : symbol),
    'JACK', 'QUEEN', 'SCATTER', 'KING',
  ],
] as const satisfies readonly (readonly ReelSymbolId[])[];

export const EXTREME_FEATURE_PROBABILITY = 0.02216729710612349;

export const REEL_WINDOW_SIZE = 3;
export const MAX_VISIBLE_SCATTERS_PER_REEL = 1;

export const FREE_SPIN_WILD_STOP_BOOSTS = Object.freeze([1.7375, 1.7375, 1.43, 1.28, 1.28] as const);

/** Exact rational representation of the active Season 3 R7 boost values (base unit 400). */
export const STOP_WEIGHT_BASE_UNITS = 400;
export const FREE_SPIN_WILD_STOP_WEIGHT_UNITS = Object.freeze([695, 695, 572, 512, 512] as const);

/** Extreme-only free-spin profile: 1.70x / 1.85x / 1.85x / 1.70x / 1.45x. */
export const EXTREME_FREE_SPIN_WILD_STOP_WEIGHT_UNITS = Object.freeze([680, 740, 740, 680, 580] as const);
